/* src/render/interiorView.ts — the walkable building interior, v2.
 *
 * v1 proved the mode: a room shaped from the real footprint, first-person walk, a floor
 * switcher. v2 makes it a place: thick walls with real window and door openings on the street
 * side, glass, frames, skirting, cornices, pendant lights, columns on big spans — and nothing
 * else until the player buys it. Stairs are purchased per storey and physically walked; shelves,
 * checkouts and storage racks appear where they are installed. The city itself is visible
 * through the windows: the frame loop in world/worldView.ts renders the real city scene from
 * this camera's pose first, and this scene (transparent background, holes where the openings
 * are) composites over it.
 *
 * The geometry lives in render/interiorArchitecture.ts as pure data; this file owns the Three
 * objects, the input, and the player. It never imports ui/ or net/: buildings arrive whole via
 * `world:enterinterior` / `interior:refresh`, and everything the player does inside goes back
 * out as events (PLAN.md Part 2 §1).
 */

import {
  AmbientLight,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  DirectionalLight,
  DoubleSide,
  DynamicDrawUsage,
  HemisphereLight,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  Object3D,
  PerspectiveCamera,
  PointLight,
  RepeatWrapping,
  Scene,
  SRGBColorSpace,
  Vector3,
  type WebGLRenderer,
} from 'three';
import { emit, on, type InteriorDescriptor } from '../core/events.ts';
import { makeOrigin, toLocal } from '../map/originRebase.ts';
import {
  buildInterior,
  FLOOR_H,
  planInterior,
  type InteriorBuild,
  type InteriorPlan,
} from './interiorArchitecture.ts';
import {
  distToSeg,
  interiorMoveVector,
  pointInPoly,
  stairProgress,
  standable,
  type Poly,
  type StairRegion,
} from './interiorGeometry.ts';
import {
  personBodyGeometry,
  personHeadGeometry,
  SHIRT_COLOURS,
  SKIN,
} from './peopleGeometry.ts';

export interface InteriorView {
  readonly active: boolean;
  /** The building being walked, for the city pass (osm id to hide, lon/lat to stand at). */
  readonly current: InteriorDescriptor | null;
  enter: (desc: InteriorDescriptor) => void;
  exit: () => void;
  update: (dt: number) => void;
  render: (renderer: WebGLRenderer) => void;
  /** Camera pose in interior-local metres, for the city pass to mirror. */
  pose: () => { x: number; y: number; z: number; yaw: number; pitch: number };
  dispose: () => void;
}

const EYE = 1.62;
const SPEED = 3.7;
const PLAYER_R = 0.42;
const LOOK = 0.0024;
const PITCH_LIMIT = 1.45;

/** A plank floor, drawn once onto a canvas — boards, grain, seams. One texture, no fetch. */
function plankTexture(): CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const boards = 6;
    const bw = size / boards;
    for (let i = 0; i < boards; i += 1) {
      // Alternate warm browns, slightly jittered per board so the floor never bands.
      const tone = 0.82 + 0.18 * ((i * 2654435761) % 100) / 100;
      ctx.fillStyle = `rgb(${Math.round(150 * tone)}, ${Math.round(112 * tone)}, ${Math.round(74 * tone)})`;
      ctx.fillRect(i * bw, 0, bw, size);
      // Grain: a few darker streaks per board.
      ctx.strokeStyle = 'rgba(60, 40, 24, 0.18)';
      ctx.lineWidth = 1;
      for (let g = 0; g < 7; g += 1) {
        const x = i * bw + ((g * 37 + i * 13) % Math.max(1, Math.round(bw)));
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.bezierCurveTo(x + 3, size * 0.3, x - 3, size * 0.6, x + 1, size);
        ctx.stroke();
      }
      // Board seam.
      ctx.fillStyle = 'rgba(40, 28, 16, 0.5)';
      ctx.fillRect(i * bw, 0, 1.4, size);
      // Butt joints at staggered heights.
      const joint = ((i * 7919) % 4) * (size / 4) + 12;
      ctx.fillRect(i * bw, joint, bw, 1.2);
    }
  }
  const tex = new CanvasTexture(canvas);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

export function createInteriorView(canvas: HTMLCanvasElement): InteriorView {
  const scene = new Scene();
  // No background and no fog: the city pass underneath provides both, and an interior fog wall
  // would grey out the very view through the windows this scene exists to frame.
  scene.background = null;

  const camera = new PerspectiveCamera(72, 1, 0.05, 500);
  camera.rotation.order = 'YXZ';

  // Daylight through the openings, warmth from the pendants. The directional is aimed when a
  // building is entered (down through the frontage), giving walls a lit side and a soft side.
  scene.add(new AmbientLight(0xffffff, 0.38));
  const hemi = new HemisphereLight(0xf2f6fa, 0x55504a, 0.55);
  scene.add(hemi);
  const dayLight = new DirectionalLight(0xfff2dd, 0.85);
  scene.add(dayLight);
  scene.add(dayLight.target);
  const torch = new PointLight(0xffedd0, 0.5, 14, 1.2);
  scene.add(torch);
  const pendantA = new PointLight(0xffe6bd, 0.85, 12, 1.15);
  const pendantB = new PointLight(0xffe6bd, 0.7, 11, 1.15);
  scene.add(pendantA, pendantB);

  const opaqueMat = new MeshLambertMaterial({ vertexColors: true, side: DoubleSide });
  const floorMat = new MeshLambertMaterial({ vertexColors: true, map: plankTexture() });
  const glassMat = new MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0.16,
    depthWrite: false,
    side: DoubleSide,
  });
  const glowMat = new MeshBasicMaterial({ vertexColors: true });

  const opaqueMesh = new Mesh(new BufferGeometry(), opaqueMat);
  const floorMesh = new Mesh(new BufferGeometry(), floorMat);
  const glassMesh = new Mesh(new BufferGeometry(), glassMat);
  const glowMesh = new Mesh(new BufferGeometry(), glowMat);
  glassMesh.renderOrder = 2;
  for (const m of [opaqueMesh, floorMesh, glassMesh, glowMesh]) {
    m.frustumCulled = false;
    scene.add(m);
  }

  /*
   * Shoppers: the same little citizens who walk the streets, rendered inside when the shop is
   * trading. Spawned at the server's customers-per-minute, walked door → shelf → checkout →
   * door. Visualisation only — every dollar they appear to spend was computed by the tick.
   */
  const MAX_SHOPPERS = 6;
  const shopperMat = new MeshLambertMaterial({ vertexColors: true });
  const shopperBodies = new InstancedMesh(personBodyGeometry(), shopperMat, MAX_SHOPPERS);
  const shopperHeads = new InstancedMesh(personHeadGeometry(), shopperMat, MAX_SHOPPERS);
  for (const m of [shopperBodies, shopperHeads]) {
    m.instanceMatrix.setUsage(DynamicDrawUsage);
    m.count = 0;
    m.frustumCulled = false;
    scene.add(m);
  }
  const shopperColour = new Color();
  for (let i = 0; i < MAX_SHOPPERS; i += 1) {
    shopperColour.setHex(SHIRT_COLOURS[(i * 3 + 1) % SHIRT_COLOURS.length] ?? 0x3f6fc9);
    shopperBodies.setColorAt(i, shopperColour);
    shopperColour.setHex(SKIN);
    shopperHeads.setColorAt(i, shopperColour);
  }
  const zeroMatrix = new Matrix4().makeScale(0, 0, 0);
  const shopperDummy = new Object3D();

  interface Shopper {
    /** Waypoints in interior-local metres; the shopper lerps leg by leg, dwelling at each stop. */
    legs: Array<{ x: number; z: number; dwell: number }>;
    leg: number;
    x: number;
    z: number;
    dwellLeft: number;
    phase: number;
  }
  const shoppers: Shopper[] = [];
  let spawnClock = 0;
  let lastSpawnTick = 0;

  let active = false;
  let desc: InteriorDescriptor | null = null;
  let plan: InteriorPlan | null = null;
  let build: InteriorBuild | null = null;
  let poly: Poly = [];
  let stairRegions: Array<{ floor: number; region: StairRegion }> = [];
  /** The doorway as a ground segment, for the walk-out corridor. */
  let doorSeg: { ax: number; az: number; bx: number; bz: number } | null = null;

  // Player pose. `py` is continuous — stairs are a ramp, not a teleport.
  let px = 0;
  let py = EYE;
  let pz = 0;
  let yaw = 0;
  let pitch = 0;
  let floorIdx = 0;

  const keys = new Set<string>();
  let pointerLocked = false;
  let aspect = 1;
  let aimId: string | null = null;

  const setGeometry = (mesh: Mesh, data: {
    positions: Float32Array; normals: Float32Array; colors: Float32Array; uvs: Float32Array | null;
  }): void => {
    mesh.geometry.dispose();
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(data.positions, 3));
    g.setAttribute('normal', new BufferAttribute(data.normals, 3));
    g.setAttribute('color', new BufferAttribute(data.colors, 3));
    if (data.uvs) g.setAttribute('uv', new BufferAttribute(data.uvs, 2));
    mesh.geometry = g;
  };

  /** (Re)build the room from a descriptor. `keepPose` holds the player still on a refresh. */
  const rebuild = (next: InteriorDescriptor, keepPose: boolean): void => {
    desc = next;
    const origin = makeOrigin(next.lon, next.lat);
    const pts: Array<readonly [number, number]> = [];
    for (const [lon, lat] of next.ring) pts.push(toLocal(lon, lat, origin));
    if (pts.length >= 2) {
      const first = pts[0]!;
      const last = pts[pts.length - 1]!;
      if (Math.abs(first[0] - last[0]) < 1e-6 && Math.abs(first[1] - last[1]) < 1e-6) pts.pop();
    }
    poly = pts.length >= 3
      ? pts
      : [[-3, -3], [3, -3], [3, 3], [-3, 3]];

    plan = planInterior(poly, next.frontageDeg, Math.max(1, Math.min(40, next.floors)));
    build = buildInterior(plan, next.fixtures);

    setGeometry(opaqueMesh, build.opaque);
    setGeometry(floorMesh, build.floor);
    setGeometry(glassMesh, build.glass);
    setGeometry(glowMesh, build.glow);

    stairRegions = [];
    if (plan.stair) {
      const s = plan.stair;
      for (const f of next.fixtures) {
        if (f.kind === 'stairs') {
          stairRegions.push({
            floor: f.floor,
            region: { x: s.x, z: s.z, dx: s.dx, dz: s.dz, len: s.len, width: s.width },
          });
        }
      }
    }

    doorSeg = null;
    if (plan.door) {
      const e = plan.edges[plan.door.edge];
      if (e) {
        doorSeg = {
          ax: e.ax + e.dx * plan.door.t0,
          az: e.az + e.dz * plan.door.t0,
          bx: e.ax + e.dx * plan.door.t1,
          bz: e.az + e.dz * plan.door.t1,
        };
      }
    }

    // Sunlight leans in through the front wall.
    const front = plan.edges[plan.frontage];
    if (front) {
      dayLight.position.set(front.nx * 30, 34, front.nz * 30);
      dayLight.target.position.set(plan.cx, 0, plan.cz);
      dayLight.target.updateMatrixWorld();
    }

    if (!keepPose) {
      px = plan.spawn.x;
      pz = plan.spawn.z;
      py = EYE;
      yaw = plan.spawn.yaw;
      pitch = -0.08;
      floorIdx = 0;
    }
    placeLights();
  };

  /** The two nearest pendants on the current floor carry the real lights. */
  function placeLights(): void {
    if (!build) return;
    const here = build.lights.filter((l) => l.floor === floorIdx);
    const a = here[0];
    const b = here[1] ?? here[0];
    if (a) pendantA.position.set(a.x, a.y, a.z);
    if (b) pendantB.position.set(b.x, b.y, b.z);
    pendantA.visible = here.length > 0;
    pendantB.visible = here.length > 1;
  }

  // ── input ───────────────────────────────────────────────────────────────────────────────

  const onKeyDown = (e: KeyboardEvent): void => {
    if (!active) return;
    const focused = document.activeElement;
    if (focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement) return;
    const k = e.key.toLowerCase();
    if (k === 'escape') {
      emit('interior:exit');
      return;
    }
    if (k === 'e') {
      if (aimId && build) {
        const hit = build.fixtureBoxes.find((f) => f.id === aimId);
        if (hit) {
          if (document.pointerLockElement === canvas) document.exitPointerLock();
          emit('interior:interact', { fixtureId: hit.id, kind: hit.kind });
        }
      }
      return;
    }
    if (k === '[' || k === 'pagedown') {
      e.preventDefault();
      requestFloor(floorIdx - 1);
      return;
    }
    if (k === ']' || k === 'pageup') {
      e.preventDefault();
      requestFloor(floorIdx + 1);
      return;
    }
    if (k >= '1' && k <= '9') {
      requestFloor(Number(k) - 1);
      return;
    }
    if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) {
      keys.add(k);
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    keys.delete(e.key.toLowerCase());
  };
  const onBlur = (): void => {
    keys.clear();
  };
  const onClick = (): void => {
    if (active && !pointerLocked) void canvas.requestPointerLock();
  };
  const onMouseMove = (e: MouseEvent): void => {
    if (!active || !pointerLocked) return;
    yaw -= e.movementX * LOOK;
    pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch - e.movementY * LOOK));
  };
  const onLockChange = (): void => {
    pointerLocked = document.pointerLockElement === canvas;
  };
  const onResize = (): void => {
    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    aspect = w / Math.max(1, h);
    camera.aspect = aspect;
    camera.updateProjectionMatrix();
  };

  function attachInput(): void {
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    window.addEventListener('resize', onResize);
    canvas.addEventListener('click', onClick);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('pointerlockchange', onLockChange);
  }
  function detachInput(): void {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('resize', onResize);
    canvas.removeEventListener('click', onClick);
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('pointerlockchange', onLockChange);
    if (document.pointerLockElement === canvas) document.exitPointerLock();
    keys.clear();
    pointerLocked = false;
  }

  // ── floors & state ──────────────────────────────────────────────────────────────────────

  function emitState(): void {
    if (!active || !desc) {
      emit('interior:state', { active: false });
      return;
    }
    emit('interior:state', {
      active: true,
      parcelId: desc.parcelId,
      name: desc.name,
      floors: Math.max(1, desc.floors),
      reachableFloors: Math.max(1, desc.reachableFloors),
      floor: floorIdx,
    });
  }

  /** The HUD's quick travel. It rides the stairs that exist; without them, it refuses. */
  function requestFloor(next: number): void {
    if (!active || !desc || !plan) return;
    const clamped = Math.max(0, Math.min(desc.floors - 1, next));
    if (clamped === floorIdx) return;
    if (clamped >= desc.reachableFloors) return;
    floorIdx = clamped;
    py = floorIdx * FLOOR_H + EYE;
    /*
     * Land at the plan's quick-travel spot — the most open point clear of the stairwell.
     * Arriving over the flight would let the ramp physics (correctly) walk the player straight
     * back down; on a shed the flight crosses the middle of the room, so "the centre" is not
     * safe either. The plan knows the one point that is.
     */
    px = plan.landing[0];
    pz = plan.landing[1];
    placeLights();
    emitState();
  }

  const offSetFloor = on('interior:setfloor', ({ floor: f }) => requestFloor(f));
  const offRefresh = on('interior:refresh', (next) => {
    if (!active) return;
    rebuild(next, true);
    emitState();
  });

  // ── movement ────────────────────────────────────────────────────────────────────────────

  function blockedByFixture(x: number, z: number, yFeet: number): boolean {
    if (!build) return false;
    for (const f of build.fixtureBoxes) {
      const b = f.box;
      const minX = (b[0] ?? 0) - PLAYER_R * 0.6;
      const minY = b[1] ?? 0;
      const minZ = (b[2] ?? 0) - PLAYER_R * 0.6;
      const maxX = (b[3] ?? 0) + PLAYER_R * 0.6;
      const maxY = b[4] ?? 0;
      const maxZ = (b[5] ?? 0) + PLAYER_R * 0.6;
      if (x > minX && x < maxX && z > minZ && z < maxZ && yFeet > minY - 0.4 && yFeet < maxY) {
        return true;
      }
    }
    return false;
  }

  function canStand(x: number, z: number, yFeet: number): boolean {
    return standable(x, z, poly, PLAYER_R) && !blockedByFixture(x, z, yFeet);
  }

  /** Step, slide, or — through the open front door — leave. */
  function tryStep(dx: number, dz: number): void {
    const yFeet = py - EYE;
    const nx = px + dx;
    const nz = pz + dz;

    /*
     * The doorway is an exit corridor. Inside it the wall-clearance rule stands down — it would
     * otherwise stop the player a body-radius short of the threshold, and no step could ever
     * land outside the footprint to trigger the exit. Inside the corridor: stay in as long as
     * the step stays in; the first step that leaves the polygon leaves the building.
     */
    if (doorSeg && floorIdx === 0
        && distToSeg(px, pz, doorSeg.ax, doorSeg.az, doorSeg.bx, doorSeg.bz) < 1.25) {
      if (!pointInPoly(nx, nz, poly)) {
        emit('interior:exit');
        return;
      }
      if (!blockedByFixture(nx, nz, yFeet)) {
        px = nx;
        pz = nz;
      }
      return;
    }

    if (canStand(nx, nz, yFeet)) {
      px = nx;
      pz = nz;
      return;
    }
    if (canStand(nx, pz, yFeet)) {
      px = nx;
      return;
    }
    if (canStand(px, nz, yFeet)) {
      pz = nz;
    }
  }

  /** A point in front of a fixture box, pulled toward the room so shoppers stand off the shelf. */
  function frontOf(box: readonly number[]): { x: number; z: number } {
    const cx2 = ((box[0] ?? 0) + (box[3] ?? 0)) / 2;
    const cz2 = ((box[2] ?? 0) + (box[5] ?? 0)) / 2;
    if (!plan) return { x: cx2, z: cz2 };
    const dx = plan.cx - cx2;
    const dz = plan.cz - cz2;
    const len = Math.hypot(dx, dz) || 1;
    return { x: cx2 + (dx / len) * 0.75, z: cz2 + (dz / len) * 0.75 };
  }

  /** Spawn and walk the visible customers. Ground floor only; the till queue is their exit. */
  function updateShoppers(dt: number): void {
    if (!desc || !build || !plan) return;
    const rate = desc.customersPerMin;
    const doorIn = plan.door && plan.edges[plan.door.edge]
      ? (() => {
          const e = plan.edges[plan.door.edge]!;
          const t = (plan.door.t0 + plan.door.t1) / 2;
          return { x: e.ax + e.dx * t - e.nx * 0.9, z: e.az + e.dz * t - e.nz * 0.9 };
        })()
      : null;

    /*
     * Arrivals run on WALL time, not physics time: physics dt is clamped per frame (rightly —
     * a background-tab catch-up must not teleport anyone through a wall), but a customer who
     * would have arrived during a stutter should still be here when the frames return.
     */
    const nowMs = performance.now();
    const wallDt = lastSpawnTick === 0 ? dt : Math.min(10, (nowMs - lastSpawnTick) / 1000);
    lastSpawnTick = nowMs;

    if (rate > 0 && doorIn && shoppers.length < MAX_SHOPPERS) {
      spawnClock += wallDt;
      const interval = 60 / Math.max(0.2, rate);
      if (spawnClock >= interval) {
        spawnClock = 0;
        const shelves = build.fixtureBoxes.filter((f) => f.kind === 'shelf' && (f.box[1] ?? 0) < 0.5);
        const tills = build.fixtureBoxes.filter((f) => f.kind === 'checkout' && (f.box[1] ?? 0) < 0.5);
        const shelf = shelves[Math.floor(Math.random() * Math.max(1, shelves.length))];
        const till = tills[0];
        if (shelf && till) {
          const browse = frontOf(shelf.box);
          const pay = frontOf(till.box);
          shoppers.push({
            legs: [
              { x: browse.x, z: browse.z, dwell: 1.6 + Math.random() * 2.2 },
              { x: pay.x, z: pay.z, dwell: 1.2 + Math.random() * 0.8 },
              { x: doorIn.x, z: doorIn.z, dwell: 0 },
            ],
            leg: 0,
            x: doorIn.x,
            z: doorIn.z,
            dwellLeft: 0,
            phase: Math.random() * Math.PI * 2,
          });
        }
      }
    }

    for (let i = shoppers.length - 1; i >= 0; i -= 1) {
      const sh = shoppers[i];
      if (!sh) continue;
      if (sh.dwellLeft > 0) {
        sh.dwellLeft -= dt;
      } else {
        const target = sh.legs[sh.leg];
        if (!target) {
          shoppers.splice(i, 1);
          continue;
        }
        const dx = target.x - sh.x;
        const dz = target.z - sh.z;
        const dist = Math.hypot(dx, dz);
        const step = 1.25 * dt;
        if (dist <= step) {
          sh.x = target.x;
          sh.z = target.z;
          sh.dwellLeft = target.dwell;
          sh.leg += 1;
          if (sh.leg >= sh.legs.length) shoppers.splice(i, 1);
        } else {
          sh.x += (dx / dist) * step;
          sh.z += (dz / dist) * step;
        }
      }
    }

    for (let i = 0; i < MAX_SHOPPERS; i += 1) {
      const sh = shoppers[i];
      if (!sh) {
        shopperBodies.setMatrixAt(i, zeroMatrix);
        shopperHeads.setMatrixAt(i, zeroMatrix);
        continue;
      }
      const target = sh.legs[sh.leg];
      const fx = target ? target.x - sh.x : 0;
      const fz = target ? target.z - sh.z : 1;
      shopperDummy.position.set(sh.x, 0.02 + Math.sin(sh.phase + performance.now() / 140) * 0.02, sh.z);
      shopperDummy.rotation.set(0, Math.atan2(-fz, fx) + Math.PI / 2, 0);
      shopperDummy.updateMatrix();
      shopperBodies.setMatrixAt(i, shopperDummy.matrix);
      shopperHeads.setMatrixAt(i, shopperDummy.matrix);
    }
    shopperBodies.count = Math.min(MAX_SHOPPERS, Math.max(shoppers.length, 0));
    shopperHeads.count = shopperBodies.count;
    shopperBodies.instanceMatrix.needsUpdate = true;
    shopperHeads.instanceMatrix.needsUpdate = true;
    if (shopperBodies.instanceColor) shopperBodies.instanceColor.needsUpdate = true;
    if (shopperHeads.instanceColor) shopperHeads.instanceColor.needsUpdate = true;
  }

  /** Crosshair ray vs fixture boxes — the "look at a shelf to use it" affordance. */
  const rayDir = new Vector3();
  function updateAim(): void {
    if (!build || build.fixtureBoxes.length === 0) {
      if (aimId !== null) {
        aimId = null;
        emit('interior:aim', null);
      }
      return;
    }
    rayDir.set(-Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), -Math.cos(yaw) * Math.cos(pitch));
    let best: { fixtureId: string; kind: string } | null = null;
    let bestT = 3.4;
    for (const f of build.fixtureBoxes) {
      const b = f.box;
      const t = rayBox(px, py, pz, rayDir.x, rayDir.y, rayDir.z,
        b[0] ?? 0, b[1] ?? 0, b[2] ?? 0, b[3] ?? 0, b[4] ?? 0, b[5] ?? 0);
      if (t !== null && t < bestT) {
        bestT = t;
        best = { fixtureId: f.id, kind: f.kind };
      }
    }
    const nextId = best?.fixtureId ?? null;
    if (nextId !== aimId) {
      aimId = nextId;
      emit('interior:aim', best);
    }
  }

  // ── the view ────────────────────────────────────────────────────────────────────────────

  const view: InteriorView = {
    get active(): boolean {
      return active;
    },
    get current(): InteriorDescriptor | null {
      return desc;
    },

    enter(next: InteriorDescriptor): void {
      if (active) detachInput();
      shoppers.length = 0;
      // Warm start: the first customer arrives within a few seconds of walking into a trading
      // shop, rather than a full average interval later. The steady rate is unchanged.
      spawnClock = next.customersPerMin > 0 ? Math.max(0, 60 / next.customersPerMin - 3) : 0;
      rebuild(next, false);
      active = true;
      onResize();
      attachInput();
      emitState();
    },

    exit(): void {
      if (!active) return;
      active = false;
      detachInput();
      desc = null;
      aimId = null;
      shoppers.length = 0;
      emit('interior:aim', null);
      emit('interior:state', { active: false });
    },

    update(dt: number): void {
      if (!active) return;
      let fwd = 0;
      let str = 0;
      if (keys.has('w') || keys.has('arrowup')) fwd += 1;
      if (keys.has('s') || keys.has('arrowdown')) fwd -= 1;
      if (keys.has('d') || keys.has('arrowright')) str += 1;
      if (keys.has('a') || keys.has('arrowleft')) str -= 1;

      if (fwd !== 0 || str !== 0) {
        const [mx, mz] = interiorMoveVector(fwd, str, yaw);
        tryStep(mx * SPEED * dt, mz * SPEED * dt);
      }

      updateShoppers(dt);

      /*
       * Height: on a staircase the feet follow the ramp; otherwise they sit on the floor slab.
       * The active flight is whichever one the player is standing over from a floor it serves —
       * so walking onto the well from above descends, and from below climbs, with no teleport.
       */
      let onStairs = false;
      for (const s of stairRegions) {
        if (floorIdx !== s.floor && floorIdx !== s.floor + 1) continue;
        const t = stairProgress(px, pz, s.region);
        if (t === null) continue;
        py = s.floor * FLOOR_H + t * FLOOR_H + EYE;
        const newFloor = t > 0.6 ? s.floor + 1 : s.floor;
        if (newFloor !== floorIdx) {
          floorIdx = newFloor;
          placeLights();
          emitState();
        }
        onStairs = true;
        break;
      }
      if (!onStairs) py = floorIdx * FLOOR_H + EYE;

      camera.position.set(px, py, pz);
      camera.rotation.set(pitch, yaw, 0);
      torch.position.set(px, py + 0.15, pz);
      updateAim();
    },

    render(renderer: WebGLRenderer): void {
      if (!active) return;
      const w = canvas.clientWidth || window.innerWidth;
      const h = canvas.clientHeight || window.innerHeight;
      const a = w / Math.max(1, h);
      if (Math.abs(a - aspect) > 1e-3) {
        aspect = a;
        camera.aspect = a;
        camera.updateProjectionMatrix();
      }
      renderer.render(scene, camera);
    },

    pose(): { x: number; y: number; z: number; yaw: number; pitch: number } {
      return { x: px, y: py, z: pz, yaw, pitch };
    },

    dispose(): void {
      offSetFloor();
      offRefresh();
      detachInput();
      for (const m of [opaqueMesh, floorMesh, glassMesh, glowMesh]) m.geometry.dispose();
      for (const m of [shopperBodies, shopperHeads]) {
        scene.remove(m);
        m.geometry.dispose();
        m.dispose();
      }
      shopperMat.dispose();
      opaqueMat.dispose();
      floorMat.map?.dispose();
      floorMat.dispose();
      glassMat.dispose();
      glowMat.dispose();
      active = false;
    },
  };

  // Test seams: pose (walking/stairs assertions), aim (interaction assertions), a look setter
  // (pointer lock cannot be driven by automation), and geometry counts (is the room built?).
  window.__gfInteriorPose = () =>
    active ? { x: px, y: py, z: pz, yaw, pitch, floor: floorIdx } : null;
  window.__gfInteriorAim = () => aimId;
  window.__gfInteriorLook = (nextYaw: number, nextPitch: number) => {
    yaw = nextYaw;
    pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, nextPitch));
  };
  window.__gfInteriorTeleport = (x: number, z: number) => {
    px = x;
    pz = z;
  };
  window.__gfInteriorDebug = () => {
    const pos = floorMesh.geometry.getAttribute('position');
    // Geometric orientation of the first floor triangle: positive = faces up = visible.
    let triY = 0;
    let minY = Infinity;
    let maxY = -Infinity;
    if (pos && pos.count >= 3) {
      const ax = pos.getX(0), az = pos.getZ(0);
      const bx = pos.getX(1), bz = pos.getZ(1);
      const cx2 = pos.getX(2), cz2 = pos.getZ(2);
      triY = (bz - az) * (cx2 - ax) - (bx - ax) * (cz2 - az);
      for (let i = 0; i < pos.count; i += 1) {
        minY = Math.min(minY, pos.getY(i));
        maxY = Math.max(maxY, pos.getY(i));
      }
    }
    return {
      opaque: opaqueMesh.geometry.getAttribute('position')?.count ?? 0,
      floor: pos?.count ?? 0,
      glass: glassMesh.geometry.getAttribute('position')?.count ?? 0,
      glow: glowMesh.geometry.getAttribute('position')?.count ?? 0,
      fixtures: build?.fixtureBoxes.length ?? 0,
      floorTriY: triY,
      floorMinY: minY,
      floorMaxY: maxY,
      stair: plan?.stair
        ? { x: plan.stair.x, z: plan.stair.z, dx: plan.stair.dx, dz: plan.stair.dz, len: plan.stair.len }
        : null,
      door: plan?.door && plan.edges[plan.door.edge]
        ? (() => {
            const e = plan.edges[plan.door.edge]!;
            const t = (plan.door.t0 + plan.door.t1) / 2;
            return { x: e.ax + e.dx * t - e.nx * 1.0, z: e.az + e.dz * t - e.nz * 1.0, nx: e.nx, nz: e.nz };
          })()
        : null,
      boxes: build?.fixtureBoxes.map((f) => ({ id: f.id, kind: f.kind, box: [...f.box] })) ?? [],
    };
  };

  return view;
}

/** Slab-method ray/AABB: distance along the ray, or null on a miss (or when inside-out). */
function rayBox(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  minX: number, minY: number, minZ: number,
  maxX: number, maxY: number, maxZ: number,
): number | null {
  let tmin = 0;
  let tmax = Infinity;
  const axes: ReadonlyArray<readonly [number, number, number, number]> = [
    [ox, dx, minX, maxX],
    [oy, dy, minY, maxY],
    [oz, dz, minZ, maxZ],
  ];
  for (const [o, d, lo, hi] of axes) {
    if (Math.abs(d) < 1e-9) {
      if (o < lo || o > hi) return null;
      continue;
    }
    const inv = 1 / d;
    let t0 = (lo - o) * inv;
    let t1 = (hi - o) * inv;
    if (t0 > t1) {
      const tmp = t0;
      t0 = t1;
      t1 = tmp;
    }
    tmin = Math.max(tmin, t0);
    tmax = Math.min(tmax, t1);
    if (tmin > tmax) return null;
  }
  return tmin;
}
